'use client'

import { HTMLAttributes } from 'react'
import {
  IconBuilding,
  IconCheck,
  IconHome2,
  IconPencil,
  IconTrash
} from '@tabler/icons-react'
import clsx from 'clsx'

import {
  CustomerAddress,
  CustomerInvoiceAddress,
  IS_PERSONAL_INVOICE_ENABLED,
  isCompanyInvoiceAddress,
  ModalAddDeliveryAddress,
  ModalAddInvoiceAddress,
  ModalRemoveDeliveryAddress,
  ModalRemoveInvoiceAddress
} from 'modules/customer'
import { FormMode } from 'shared/types/forms'
import { useModal } from 'shared/ui/Modal/useModal'
import { theme } from 'shared/ui/utils/designTokens'

import styles from './CartAddressOption.module.scss'

interface CartAddressOptionProps extends HTMLAttributes<HTMLDivElement> {
  deliveryAddress?: CustomerAddress
  invoiceAddress?: CustomerInvoiceAddress
  checked: boolean
  handleAssignAddressToCart?: (
    addressId: string,
    postalCode: string
  ) => () => Promise<void>
  showRemoveDeliveryIcon?: boolean
}

const CartAddressOption = ({
  deliveryAddress,
  invoiceAddress,
  checked,
  handleAssignAddressToCart,
  showRemoveDeliveryIcon = true,
  className,
  ...props
}: CartAddressOptionProps) => {
  const {
    isModalOpen: isModalAddDeliveryAddressOpen,
    openModal: openModalAddDeliveryAddress,
    closeModal: closeModalAddDeliveryAddress
  } = useModal()

  const isInvoiceTypeCompany =
    invoiceAddress && isCompanyInvoiceAddress(invoiceAddress)

  return (
    <div
      className={clsx(styles.savedAddress, className)}
      onClick={
        handleAssignAddressToCart
          ? handleAssignAddressToCart(
              deliveryAddress?.id || invoiceAddress?.id || '',
              deliveryAddress?.postalCode || invoiceAddress?.postalCode || ''
            )
          : undefined
      }
      {...props}
    >
      {/* {checked && <Checked>Aktualnie wybrany</Checked>} */}
      <div className={styles.entryWrapper}>
        <div className={styles.icon} color={theme.colors.text.base}>
          {deliveryAddress?.iconType === 'home' ||
          invoiceAddress?.iconType === 'home' ? (
            <IconHome2 />
          ) : (
            <IconBuilding />
          )}
        </div>
        <div className={styles.addressWrapper}>
          {deliveryAddress && (
            <>
              <div className={styles.addressName}>
                {deliveryAddress?.name || invoiceAddress?.name}
              </div>
              <div className={styles.address}>
                {`${deliveryAddress?.firstName} ${deliveryAddress?.lastName}`}
              </div>
              <div className={styles.address}>
                {`${deliveryAddress?.street} ${deliveryAddress?.houseNumber} ${
                  deliveryAddress?.flatNumber
                    ? `m. ${deliveryAddress?.flatNumber}`
                    : ''
                }, ${deliveryAddress?.postalCode} ${deliveryAddress?.city}`}
              </div>
              <div className={styles.address}>
                {`+48${deliveryAddress?.phoneNumber}`}
              </div>
            </>
          )}
          {invoiceAddress && (
            <>
              <div className={styles.addressNameWrapper}>
                <div className={styles.addressName}>
                  {invoiceAddress?.name}
                </div>
                {IS_PERSONAL_INVOICE_ENABLED && (
                  <div className={styles.invoiceTypeLabel}>
                    {isInvoiceTypeCompany ? 'Firmowa' : 'Imienna'}
                  </div>
                )}
              </div>
              {isInvoiceTypeCompany ? (
                <>
                  <div className={styles.address}>
                    {invoiceAddress?.companyName}
                  </div>
                  <div className={styles.address}>
                    NIP: {invoiceAddress?.nip}
                  </div>
                </>
              ) : (
                <div className={styles.address}>
                  {invoiceAddress?.firstName} {invoiceAddress?.lastName}
                </div>
              )}
              <div className={styles.address}>
                {`${invoiceAddress?.street} ${invoiceAddress?.houseNumber} ${
                  invoiceAddress?.flatNumber
                    ? `m. ${invoiceAddress?.flatNumber}`
                    : ''
                }, ${invoiceAddress?.postalCode} ${invoiceAddress?.city}`}
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.buttonWrapper}>
        {checked && deliveryAddress ? (
          <>
            <ModalAddDeliveryAddress
              isOpen={isModalAddDeliveryAddressOpen}
              handleOpen={openModalAddDeliveryAddress}
              handleClose={closeModalAddDeliveryAddress}
              address={deliveryAddress}
              mode={FormMode.edit}
              trigger={
                <div className={styles.editButton} aria-label='Edytuj adres'>
                  <IconPencil color={theme.colors.text.base} />
                </div>
              }
            />
            {showRemoveDeliveryIcon && (
              <ModalRemoveDeliveryAddress
                address={deliveryAddress}
                trigger={
                  <div className={styles.removeButton} aria-label='Usuń adres'>
                    <IconTrash color={theme.colors.text.base} />
                  </div>
                }
              />
            )}

            <div className={styles.defaultInfoTagWeb}>
              <IconCheck />
            </div>
          </>
        ) : checked && invoiceAddress ? (
          <>
            <ModalAddInvoiceAddress
              address={invoiceAddress}
              mode={FormMode.edit}
              trigger={
                <div className={styles.editButton} aria-label='Edytuj adres'>
                  <IconPencil color={theme.colors.text.base} />
                </div>
              }
            />
            <ModalRemoveInvoiceAddress
              address={invoiceAddress}
              trigger={
                <div className={styles.removeButton} aria-label='Usuń adres'>
                  <IconTrash color={theme.colors.text.base} />
                </div>
              }
            />
            <div className={styles.defaultInfoTagWeb}>
              <IconCheck />
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default CartAddressOption
